import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { ReferenceDataOptionLists } from '../../../../../lib/constants/reference-data-option-lists';
import { ReferenceDataService } from '../../../../../lib/services/reference-data.service';
import { CaseProduct } from '../../../models/case-product.model';
import { MortgageApplicationContext } from '../../../models/mortgage-application-context.model';
import { BaseMortgageApplicationComponent } from '../../../pages/base-mortgage-application.component';
import { MortgageApplicationFormService } from '../../../services/mortgage-application-form.service';
import { PartAndPartProductService } from './../../../../shared/services/part-and-part-product.service';
import { Applicant } from './../../../models/applicant.model';
import { ApplicationProduct } from './../../../models/application-product.model';
import { MortgageApplicationStore } from './../../../services/mortgage-application-store';

@Component({
  selector: 'boi-about-the-mortgage',
  templateUrl: './about-the-mortgage.component.html',
  styleUrls: ['./about-the-mortgage.component.scss']
})
export class AboutTheMortgageComponent extends BaseMortgageApplicationComponent {
  public isProceedStatementVisible: boolean;
  public applicantAccountHolderIdentifier: number;
  public repaymentTypeOptionList: string;
  public isAmendButtonAvailable = false;
  public areNichaFieldsDisabled = false;
  public isDirectContext = false;
  public areAdditionalBorrowingsVisible: boolean;

  @Input() isFeeUpToDate;
  @Output() readonly isFeeUpToDateChange = new EventEmitter<boolean>();

  constructor(
    protected mortgageApplicationStore: MortgageApplicationStore,
    private readonly referenceDataService: ReferenceDataService,
    private readonly cdr: ChangeDetectorRef,
    private readonly mortgageApplicationFormService: MortgageApplicationFormService,
    public partAndPartProductService: PartAndPartProductService
  ) {
    super(mortgageApplicationStore);

    this.mortgageApplicationStore
      .notifyAmendProductCompleteObservable()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.onProductChange();
        this.cdr.detectChanges();
      });
  }

  get applicationProducts(): Array<ApplicationProduct> {
    return this.mortgageApplication ? this.mortgageApplication.ApplicationProducts : undefined;
  }

  protected initialize(): void {
    super.initialize();
    this.setApplicantAccountHolderIdentifier();
    this.partAndPartProductService.application = this.mortgageApplication;
    this.partAndPartProductService.firstProduct = this.mortgageApplication.ApplicationProducts[0].CaseProduct;
    this.partAndPartProductService.setRepaymentTypeFlags();
    if (this.partAndPartProductService.isProductPartRepaymentPartInterest) {
      this.partAndPartProductService.secondProduct = this.mortgageApplication.ApplicationProducts[1].CaseProduct;
    }
    this.setRepaymentTypeOptionList();
    this.isDirectContext =
      this.mortgageApplicationFormService.getCurrentContext() === MortgageApplicationContext.Direct;
    this.isAmendButtonAvailable =
      this.isDirectContext &&
      (this.mortgageApplication.IsAip || this.mortgageApplication.IsFullApp) &&
      !this.mortgageApplication.IsFactFindReadOnly;
    this.areNichaFieldsDisabled =
      this.mortgageApplicationFormService.getCurrentContext() === MortgageApplicationContext.Direct &&
      this.mortgageApplication.IsFactFind;
    this.changeAdditionalBorrowingsVisiblity();
  }

  public resetRepaymentType(): void {
    const repaymentTypeOptions = this.referenceDataService.getOptionListConfigItems(this.repaymentTypeOptionList);
    if (
      repaymentTypeOptions
        .map((repaymentType) => repaymentType.Code)
        .indexOf(this.mortgageApplication.RepaymentTypeCodeCache) === -1
    ) {
      this.mortgageApplication.RepaymentTypeCodeCache = undefined;
    }
  }

  public onProductChange(): void {
    this.isFeeUpToDateChange.emit(false);
    this.setRepaymentTypeOptionList();
    this.resetRepaymentType();
  }

  private setRepaymentTypeOptionList(): void {
    this.repaymentTypeOptionList = this.isLoanGroupTypeHelpToBuy()
      ? this.referenceDataOptionLists.RepaymentTypeHtb
      : this.referenceDataOptionLists.RepaymentType;
  }

  public areRemainderFundingDetailsVisible(): boolean {
    let areFundingDetailsVisible = false;
    if (this.mortgageApplication && this.mortgageApplication.isRemortgage && !this.mortgageApplication.isResidential) {
      areFundingDetailsVisible =
        this.mortgageApplication.LoanAmountCache < this.mortgageApplication.MortgagePayOffAmount;
    }

    return areFundingDetailsVisible;
  }

  public changeProceedStatementVisible(): void {
    this.isProceedStatementVisible =
      this.mortgageApplication.InterestAnyOtherPropertyCode === this.referenceDataOptionLists.AnswerTypeYes
        ? true
        : false;
  }

  public onMortgagePayOffAmountBlurChange(): void {
    this.changeAdditionalBorrowingsVisiblity();
  }

  public changeAdditionalBorrowingsVisiblity(): void {
    if (
      this.mortgageApplication &&
      this.mortgageApplication.MortgagePayOffAmount !== undefined &&
      this.mortgageApplication.MortgagePayOffAmount !== null
    ) {
      this.areAdditionalBorrowingsVisible =
        this.mortgageApplication.isRemortgage &&
        this.mortgageApplication.LoanAmountCache > this.mortgageApplication.MortgagePayOffAmount;
    } else {
      this.areAdditionalBorrowingsVisible = false;
    }
  }

  public areBtlAdditionalFieldsVisible(): boolean {
    return this.mortgageApplication.HowManyBtlWithUs > 1;
  }

  public isTotalBalanceOutstandingBtlVisible(): boolean {
    return this.mortgageApplication.HowManyBtlInTotal > 0;
  }

  public areDepositSourcesVisible(): boolean {
    const isPurchaseAndHasLoanAmount =
      this.mortgageApplication && this.mortgageApplication.isPurchase && this.mortgageApplication.LoanAmountCache;

    return (
      isPurchaseAndHasLoanAmount &&
      this.mortgageApplication.PurchasePropertyPrice - this.mortgageApplication.LoanAmountCache > 0
    );
  }

  public onAmendClick(): void {
    this.mortgageApplicationStore.notifyAmendProduct();
  }

  onDprRelatedFieldValueChanged(): void {
    this.isFeeUpToDate = false;
    this.isFeeUpToDateChange.emit(this.isFeeUpToDate);
  }

  updateMortgagePayOffDependencies(): void {
    if (this.mortgageApplication && this.mortgageApplication.Applicants) {
      for (const applicant of this.mortgageApplication.Applicants) {
        this.updateMortgagePayOffForApplicant(applicant);
      }
    }
  }

  private updateMortgagePayOffForApplicant(applicant: Applicant): void {
    if (
      this.mortgageApplication.isBuyToLet &&
      applicant.IsPartToRemortgageCode === ReferenceDataOptionLists.AnswerTypeYes
    ) {
      applicant.ResidentialMortgageOwed = this.mortgageApplication.MortgagePayOffAmount;
      applicant.HoldMortgageBTLCode =
        this.mortgageApplication.MortgagePayOffAmount > 0
          ? ReferenceDataOptionLists.AnswerTypeYes
          : ReferenceDataOptionLists.AnswerTypeNo;
    }
  }

  public setLoanAmount(newAmount: number): void {
    this.partAndPartProductService.setLoanAmount(newAmount);
    this.cdr.detectChanges();
  }

  public setRepaymentType(newRepaymentType: string): void {
    this.partAndPartProductService.setRepaymentType(
      newRepaymentType,
      this.addRepaymentPartProduct,
      this.deleteRepaymentPartProduct,
      this.mortgageApplication.ApplicationProducts
    );
    this.cdr.detectChanges();
  }

  public addRepaymentPartProduct(
    applicationProducts: Array<ApplicationProduct>,
    repaymentPartProduct: CaseProduct
  ): void {
    const secondAppProduct = new ApplicationProduct();
    secondAppProduct.ApplicationNumber = applicationProducts[0].ApplicationNumber;
    secondAppProduct.AreRepaymentAssetsInSterlingCode = applicationProducts[0].AreRepaymentAssetsInSterlingCode;
    secondAppProduct.RepaymentMonthlyCost = applicationProducts[0].RepaymentMonthlyCost;
    secondAppProduct.RepaymentMaturityDate = applicationProducts[0].RepaymentMaturityDate;
    secondAppProduct.RepaymentProjectedValue = applicationProducts[0].RepaymentProjectedValue;

    secondAppProduct.CaseProduct = repaymentPartProduct;
    applicationProducts.push(secondAppProduct);
  }

  public deleteRepaymentPartProduct(applicationProducts: Array<ApplicationProduct>): void {
    applicationProducts.pop();
  }

  public setInterestOnlyPart(value: number): void {
    this.partAndPartProductService.setInterestOnlyPart(value);
    this.cdr.detectChanges();
  }

  public changeMoreThanOneSignatoryForDirectDebitCode(): void {
    if (this.mortgageApplication.MoreThanOneSignatoryForDirectDebitCode === ReferenceDataOptionLists.AnswerTypeYes) {
      this.applicantAccountHolderIdentifier = undefined;
    }
  }

  public changeApplicantAccountHolder(): void {
    this.mortgageApplication.Applicants.forEach((applicant) => {
      applicant.IsAccountHolder =
        this.applicantAccountHolderIdentifier === applicant.ApplicantTransactOrder ? true : false;
    });
  }

  private setApplicantAccountHolderIdentifier(): void {
    this.mortgageApplication.Applicants.forEach((applicant) => {
      if (applicant.IsAccountHolder) {
        this.applicantAccountHolderIdentifier = applicant.ApplicantTransactOrder;
      }
    });
  }
}
